import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "sonner";
import { triggerDocumentExtraction } from "@/api/documents";
import {
    isExtractionTriggerConflict,
    isExtractionUnavailableDetail,
    isExtractionUnavailableResponse,
    type FieldExtractionStatus,
} from "@/api/documents/documents.types";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2 } from "lucide-react";
import {
    formatExtractionToast,
    formatExtractionConflictToast,
    formatExtractionUnavailableToast,
} from "./extraction-toast-helpers";

interface RunExtractionButtonProps {
    documentId: number;
    fieldExtractionStatus: FieldExtractionStatus;
    fieldExtractionAvailable: boolean;
    formExtractionAvailable: boolean;
    /** Idle/pending state label. Defaults to "Run extraction"; the section's
        empty-extract state passes "Extract fields" so the empty stub reads
        state→action. */
    pendingLabel?: string;
}

export function RunExtractionButton({
    documentId,
    fieldExtractionStatus,
    fieldExtractionAvailable,
    formExtractionAvailable,
    pendingLabel = "Run extraction",
}: RunExtractionButtonProps) {
    const queryClient = useQueryClient();
    const anyPipelineAvailable =
        fieldExtractionAvailable || formExtractionAvailable;

    const mutation = useMutation({
        mutationFn: () => triggerDocumentExtraction(documentId),
        onSuccess: (response) => {
            queryClient.invalidateQueries({
                queryKey: ["frontend-document-extracted-fields", documentId],
            });
            queryClient.invalidateQueries({
                queryKey: ["frontend-document-coi", documentId],
            });
            toast.info(formatExtractionToast(response));
        },
        onError: (err) => {
            if (axios.isAxiosError(err)) {
                const status = err.response?.status;
                const data = err.response?.data;
                if (status === 409 && isExtractionTriggerConflict(data)) {
                    toast.info(formatExtractionConflictToast(data));
                    return;
                }
                if (status === 400 && isExtractionUnavailableResponse(data)) {
                    toast.warning(formatExtractionUnavailableToast(data));
                    return;
                }
                if (status === 400 && isExtractionUnavailableDetail(data)) {
                    toast.warning(data.detail);
                    return;
                }
                if (status === 404) {
                    toast.error("Document not found");
                    return;
                }
            }
            toast.error(
                "Could not start extraction. Please try again or contact support."
            );
        },
    });

    if (fieldExtractionStatus === "complete") return null;
    if (!anyPipelineAvailable) return null;

    if (fieldExtractionStatus === "processing") {
        return (
            <Button disabled>
                <Loader2 className="size-4 animate-spin" />
                Extracting…
            </Button>
        );
    }

    const label =
        fieldExtractionStatus === "failed" ? "Re-extract" : pendingLabel;

    return (
        <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            <Sparkles className="size-4" />
            {label}
        </Button>
    );
}
